import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { isImageMime } from '@hygge/shared';
import { ChevronLeft, ChevronRight, Download, ExternalLink, FileText, Minus, Plus } from 'lucide-react';
import { getDocument, GlobalWorkerOptions, TextLayer } from 'pdfjs-dist';
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import 'pdfjs-dist/web/pdf_viewer.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

GlobalWorkerOptions.workerSrc = workerUrl;

interface CvViewerProps {
  fileUrl: string;
  filename?: string;
  /** MIME type drives renderer choice: PDF → pdfjs, image/* → <img>, else → download card. */
  mimeType?: string;
  /** Render only first N pages (used for the Profile inline preview). */
  maxPages?: number;
  /** Embedded mode: hide toolbar, compact container. */
  embedded?: boolean;
  className?: string;
}

export function CvViewer(props: CvViewerProps) {
  const { fileUrl, filename, mimeType, embedded, className } = props;

  if (!mimeType || mimeType === 'application/pdf') {
    return <PdfDocumentViewer {...props} />;
  }

  if (isImageMime(mimeType)) {
    return (
      <ImageViewer
        fileUrl={fileUrl}
        filename={filename}
        embedded={embedded}
        className={className}
      />
    );
  }

  if (mimeType === DOCX_MIME) {
    return (
      <DocxViewer
        fileUrl={fileUrl}
        filename={filename}
        embedded={embedded}
        className={className}
      />
    );
  }

  return (
    <DownloadFallback
      fileUrl={fileUrl}
      filename={filename}
      embedded={embedded}
      className={className}
    />
  );
}

const DEFAULT_SCALE = 1.25;
const MIN_SCALE = 0.5;
const MAX_SCALE = 3;

function PdfDocumentViewer({
  fileUrl,
  filename,
  maxPages,
  embedded,
  className,
}: CvViewerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(embedded ? 1 : DEFAULT_SCALE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const pagesToRender = useMemo(() => {
    if (!doc) return 0;
    return maxPages ? Math.min(maxPages, doc.numPages) : doc.numPages;
  }, [doc, maxPages]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const task = getDocument({ url: fileUrl });
    task.promise
      .then((pdf) => {
        if (cancelled) {
          pdf.destroy();
          return;
        }
        setDoc(pdf);
        setTotalPages(pdf.numPages);
        setCurrentPage(1);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'Failed to load PDF';
        setError(msg);
        setLoading(false);
      });
    return () => {
      cancelled = true;
      task.destroy().catch(() => undefined);
    };
  }, [fileUrl]);

  useEffect(() => {
    if (!doc || !containerRef.current) return;
    const container = containerRef.current;
    container.innerHTML = '';
    let cancelled = false;

    async function renderAll() {
      if (!doc) return;
      const max = maxPages ? Math.min(maxPages, doc.numPages) : doc.numPages;
      for (let i = 1; i <= max; i++) {
        if (cancelled) return;
        const page: PDFPageProxy = await doc.getPage(i);
        if (cancelled) return;
        const viewport = page.getViewport({ scale });

        const pageWrap = document.createElement('div');
        pageWrap.className = 'relative mx-auto mb-4 shadow-sm ring-1 ring-border bg-white';
        pageWrap.style.width = `${viewport.width}px`;
        pageWrap.style.height = `${viewport.height}px`;

        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.className = 'absolute inset-0';
        pageWrap.appendChild(canvas);

        const textLayerDiv = document.createElement('div');
        textLayerDiv.className = 'textLayer';
        textLayerDiv.style.width = `${viewport.width}px`;
        textLayerDiv.style.height = `${viewport.height}px`;
        textLayerDiv.setAttribute('data-page', String(i));
        pageWrap.appendChild(textLayerDiv);

        container.appendChild(pageWrap);

        const canvasContext = canvas.getContext('2d');
        if (!canvasContext) continue;
        await page.render({ canvasContext, viewport, canvas }).promise;
        if (cancelled) return;

        const textLayer = new TextLayer({
          textContentSource: await page.getTextContent(),
          container: textLayerDiv,
          viewport,
        });
        await textLayer.render();
      }
    }

    renderAll().catch((err: unknown) => {
      if (cancelled) return;
      const msg = err instanceof Error ? err.message : 'Failed to render PDF';
      setError(msg);
    });

    return () => {
      cancelled = true;
    };
  }, [doc, scale, maxPages]);

  const scrollToPage = useCallback((pageNumber: number) => {
    const container = containerRef.current;
    if (!container) return;
    const target = container.querySelector<HTMLDivElement>(`[data-page="${pageNumber}"]`);
    if (target?.parentElement) {
      target.parentElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const handlePrev = () => {
    const next = Math.max(1, currentPage - 1);
    setCurrentPage(next);
    scrollToPage(next);
  };

  const handleNext = () => {
    const next = Math.min(pagesToRender, currentPage + 1);
    setCurrentPage(next);
    scrollToPage(next);
  };

  const zoomOut = () => setScale((s) => Math.max(MIN_SCALE, s - 0.25));
  const zoomIn = () => setScale((s) => Math.min(MAX_SCALE, s + 0.25));

  if (error) {
    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center rounded-md border border-dashed border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground',
          className,
        )}
      >
        <p className="font-medium text-foreground">Couldn't load PDF</p>
        <p className="mt-1 text-xs">{error}</p>
        <a
          href={fileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-1 text-xs text-hygge hover:underline"
        >
          Open in new tab <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex flex-col overflow-hidden rounded-md border border-border bg-muted/20',
        embedded ? 'max-h-[420px]' : 'h-full',
        className,
      )}
    >
      {!embedded ? (
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border bg-background/80 px-3 py-2 text-xs backdrop-blur">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={handlePrev}
              disabled={currentPage <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="min-w-[60px] text-center tabular-nums text-muted-foreground">
              {pagesToRender > 0 ? `${currentPage} / ${pagesToRender}` : '—'}
            </span>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={handleNext}
              disabled={currentPage >= pagesToRender}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="min-w-0 truncate px-2 text-xs text-muted-foreground">
            {filename ?? 'Document'}
          </div>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={zoomOut}
              disabled={scale <= MIN_SCALE}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <span className="w-[48px] text-center tabular-nums text-muted-foreground">
              {Math.round(scale * 100)}%
            </span>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={zoomIn}
              disabled={scale >= MAX_SCALE}
            >
              <Plus className="h-4 w-4" />
            </Button>
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2 inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-foreground hover:bg-muted"
            >
              Open full CV <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      ) : null}

      <div
        ref={containerRef}
        className={cn(
          'relative flex-1 overflow-auto px-2 py-3',
          embedded && 'pointer-events-auto',
        )}
      >
        {loading ? (
          <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
            Loading PDF...
          </div>
        ) : null}
      </div>

      {embedded && totalPages > (maxPages ?? 0) ? (
        <div className="shrink-0 border-t border-border bg-background/80 px-3 py-1.5 text-[11px] text-muted-foreground">
          Showing first {maxPages} of {totalPages} pages
        </div>
      ) : null}
    </div>
  );
}

interface ImageViewerProps {
  fileUrl: string;
  filename?: string;
  embedded?: boolean;
  className?: string;
}

function ImageViewer({ fileUrl, filename, embedded, className }: ImageViewerProps) {
  return (
    <div
      className={cn(
        'flex flex-col overflow-hidden rounded-md border border-border bg-muted/20',
        embedded ? 'max-h-[420px]' : 'h-full',
        className,
      )}
    >
      <div className="flex flex-1 items-center justify-center overflow-auto p-3">
        <img
          src={fileUrl}
          alt={filename ?? 'CV image'}
          className="max-h-full max-w-full object-contain shadow-sm ring-1 ring-border"
        />
      </div>
    </div>
  );
}

interface DocxViewerProps {
  fileUrl: string;
  filename?: string;
  embedded?: boolean;
  className?: string;
}

function DocxViewer({ fileUrl, filename, embedded, className }: DocxViewerProps) {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setHtml(null);
    setError(null);
    (async () => {
      try {
        const res = await api.get<ArrayBuffer>(fileUrl, {
          responseType: 'arraybuffer',
          baseURL: '',
        });
        const [{ convertToHtml }, { default: DOMPurify }] = await Promise.all([
          import('mammoth'),
          import('dompurify'),
        ]);
        const result = await convertToHtml({ arrayBuffer: res.data });
        if (cancelled) return;
        const safe = DOMPurify.sanitize(result.value, {
          USE_PROFILES: { html: true },
        });
        setHtml(safe);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to render document');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fileUrl]);

  if (error) {
    return (
      <DownloadFallback
        fileUrl={fileUrl}
        filename={filename}
        embedded={embedded}
        className={className}
      />
    );
  }

  return (
    <div
      className={cn(
        'flex flex-col overflow-hidden rounded-md border border-border bg-muted/20',
        embedded ? 'max-h-[420px]' : 'h-full',
        className,
      )}
    >
      <div className="flex-1 overflow-auto bg-white px-6 py-5">
        {html === null ? (
          <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
            Rendering DOCX...
          </div>
        ) : (
          // HW-515: paper surface is bg-white in both themes, so pin a fixed dark
          // body colour here — theme-driven `text-foreground` would otherwise read
          // near-white on white in dark mode. `prose prose-sm` were no-ops
          // (@tailwindcss/typography is not registered in globals.css), dropped.
          <div
            className="docx-preview max-w-none text-[#111827]"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        )}
      </div>
    </div>
  );
}

interface DownloadFallbackProps {
  fileUrl: string;
  filename?: string;
  embedded?: boolean;
  className?: string;
}

function DownloadFallback({ fileUrl, filename, embedded, className }: DownloadFallbackProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-md border border-dashed border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground',
        embedded ? 'max-h-[420px]' : 'h-full',
        className,
      )}
    >
      <FileText className="h-10 w-10 text-muted-foreground/60" aria-hidden />
      <div>
        <p className="font-medium text-foreground">{filename ?? 'Document'}</p>
        <p className="mt-1 text-xs">Inline preview is not available for this file type.</p>
      </div>
      <a
        href={fileUrl}
        download={filename}
        className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
      >
        <Download className="h-3.5 w-3.5" />
        Download to view
      </a>
    </div>
  );
}
